import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  type SavedLensProposal,
  useLensCollaborationMutation,
  useLensProposals,
  useLensRevision,
  useLensRevisions,
} from '~/hooks/apis/reporting/useLensCollaboration';

import { LENS_CARD_TYPE_OPTIONS, type Lens, type LensResponse } from '~/types/leLens';

import LensProposalVisualPreview from './LensProposalVisualPreview';
import LensStructuralProposalPreview from './LensStructuralProposalPreview';
import { editableLensDefinition, lensChangeLabel, lensDefinitionChanges } from './lensDefinitionChanges';

export default function LensCollaborationDialog({
  lens,
  access,
  open,
  onClose,
}: {
  lens: Lens;
  access: LensResponse['access'];
  open: boolean;
  onClose: () => void;
}) {
  const [reviewMode, setReviewMode] = useState<'diff' | 'structure' | 'visual'>('diff');
  const [selected, setSelected] = useState<SavedLensProposal | null>(null);
  const [revision, setRevision] = useState<number | null>(null);
  const [cardId, setCardId] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState('');
  const [summary, setSummary] = useState('');
  const [notice, setNotice] = useState('');
  const [proposalOffset, setProposalOffset] = useState(0);
  const [revisionOffset, setRevisionOffset] = useState(0);
  const proposals = useLensProposals(lens.id, open, proposalOffset);
  const revisions = useLensRevisions(lens.id, open && access.canEdit, revisionOffset);
  const snapshot = useLensRevision(lens.id, revision);
  const mutation = useLensCollaborationMutation(lens.id);
  const current = { title: lens.title, description: lens.description, spec: lens.spec };
  const candidate =
    selected?.definition ??
    (snapshot.data
      ? {
          title: snapshot.data.lens.title,
          description: snapshot.data.lens.description,
          spec: snapshot.data.lens.spec,
        }
      : undefined);
  const changes = candidate
    ? lensDefinitionChanges(editableLensDefinition(current), editableLensDefinition(candidate))
    : [];
  const failure = mutation.error ?? proposals.error ?? revisions.error ?? snapshot.error;
  const run = async (suffix: string, body: unknown) => {
    try {
      await mutation.mutateAsync({ suffix, body });
      setSelected(null);
      setRevision(null);
      setNotice('Change recorded.');
      if (suffix === 'proposals') {
        setCardId('');
        setTitle('');
        setType('');
        setSummary('');
      }
    } catch {
      setNotice('');
    }
  };
  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => !mutation.isPending && onClose()}>
      <DialogTitle>{lens.title} · Version history</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            Viewers can propose changes. Owners review their dashboards; reporting admins can also review published
            dashboards.
          </Typography>
          {notice && <Alert severity="success">{notice}</Alert>}
          {failure && (
            <Alert severity="error">
              {failure instanceof Error ? failure.message : 'Could not complete this request.'}
            </Alert>
          )}
          {proposals.data?.proposals.map((proposal) => (
            <Stack direction="row" spacing={1} alignItems="center" key={proposal.id}>
              <Button
                onClick={() => {
                  setSelected(proposal);
                  setRevision(null);
                }}
              >
                {proposal.summary}
              </Button>
              <Typography variant="body2">
                {proposal.authorEmail} · {proposal.status}
                {proposal.stale ? ' · outdated' : ''}
              </Typography>
            </Stack>
          ))}
          {proposals.data?.proposals.length === 0 && <Typography variant="body2">No proposals yet.</Typography>}
          {(proposalOffset > 0 || proposals.data?.nextOffset != null) && (
            <Stack direction="row">
              <Button
                disabled={proposalOffset === 0}
                onClick={() => setProposalOffset(Math.max(0, proposalOffset - 100))}
              >
                Newer proposals
              </Button>
              <Button
                disabled={proposals.data?.nextOffset == null}
                onClick={() => setProposalOffset(proposals.data!.nextOffset!)}
              >
                Older proposals
              </Button>
            </Stack>
          )}
          {access.canEdit && (
            <TextField
              select
              label="Inspect a saved version"
              value={revision ?? ''}
              onChange={(event) => {
                setRevision(event.target.value ? Number(event.target.value) : null);
                setSelected(null);
              }}
            >
              <MenuItem value="">Choose a version</MenuItem>
              {revisions.data?.revisions.map((item) => (
                <MenuItem key={item.version} value={item.version}>
                  Version {item.version} · {item.actorEmail} · {new Date(item.createdAt).toLocaleString()}
                </MenuItem>
              ))}
            </TextField>
          )}
          {access.canEdit && (revisionOffset > 0 || revisions.data?.nextOffset != null) && (
            <Stack direction="row">
              <Button
                disabled={revisionOffset === 0}
                onClick={() => setRevisionOffset(Math.max(0, revisionOffset - 100))}
              >
                Newer versions
              </Button>
              <Button
                disabled={revisions.data?.nextOffset == null}
                onClick={() => setRevisionOffset(revisions.data!.nextOffset!)}
              >
                Older versions
              </Button>
            </Stack>
          )}
          {selected && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={reviewMode}
              onChange={(_, value) => value && setReviewMode(value)}
              aria-label="Review mode"
            >
              <ToggleButton value="diff">Definition changes</ToggleButton>
              <ToggleButton value="structure">Structure preview</ToggleButton>
              <ToggleButton value="visual">Visual preview</ToggleButton>
            </ToggleButtonGroup>
          )}
          {selected && reviewMode === 'visual' && (
            <LensProposalVisualPreview key={selected.id} lens={lens} proposal={selected} />
          )}
          {selected && reviewMode === 'structure' && (
            <LensStructuralProposalPreview lens={lens} definition={selected.definition} />
          )}
          {candidate && (!selected || reviewMode === 'diff') && (
            <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Setting</TableCell>
                    <TableCell>Current</TableCell>
                    <TableCell>{selected ? 'Proposed' : `Version ${revision}`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changes.map((change) => (
                    <TableRow key={change.field}>
                      <TableCell title={change.field}>{lensChangeLabel(change.field)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{change.before}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{change.after}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {changes.length === 0 && <Typography>No definition changes.</Typography>}
            </Box>
          )}
          {selected?.status === 'pending' && (
            <Stack direction="row" spacing={1}>
              {access.canReview ? (
                <>
                  <Button
                    variant="contained"
                    disabled={mutation.isPending || selected.baseVersion !== lens.version}
                    onClick={() =>
                      void run(`proposals/${selected.id}/review`, { action: 'accept', baseVersion: lens.version })
                    }
                  >
                    Accept proposal
                  </Button>
                  <Button
                    disabled={mutation.isPending}
                    onClick={() => void run(`proposals/${selected.id}/review`, { action: 'reject' })}
                  >
                    Reject
                  </Button>
                </>
              ) : (
                <Button
                  disabled={mutation.isPending}
                  onClick={() => void run(`proposals/${selected.id}/review`, { action: 'withdraw' })}
                >
                  Withdraw proposal
                </Button>
              )}
            </Stack>
          )}
          {revision !== null && snapshot.data && access.canEdit && (
            <Stack spacing={1}>
              <Typography variant="body2">
                Restore creates a new version. Ownership and publication stay unchanged. Warehouse data is not rolled
                back.
              </Typography>
              <Button
                variant="contained"
                disabled={mutation.isPending || revision === lens.version}
                onClick={() => void run(`revisions/${revision}/restore`, { baseVersion: lens.version })}
              >
                Restore version {revision}
              </Button>
            </Stack>
          )}
          {access.canPropose && (
            <Stack spacing={1}>
              <Typography variant="subtitle1">Propose a card change</Typography>
              <TextField
                select
                label="Card"
                value={cardId}
                onChange={(event) => {
                  const card = lens.spec.cards.find((entry) => entry.id === event.target.value);
                  setCardId(event.target.value);
                  setTitle(card?.title ?? '');
                  setType(card?.type ?? '');
                }}
              >
                <MenuItem value="">Choose a card</MenuItem>
                {lens.spec.cards.map((card) => (
                  <MenuItem key={card.id} value={card.id}>
                    {card.title}
                  </MenuItem>
                ))}
              </TextField>
              {cardId && (
                <>
                  <TextField label="Proposed title" value={title} onChange={(event) => setTitle(event.target.value)} />
                  <TextField select label="Chart type" value={type} onChange={(event) => setType(event.target.value)}>
                    {LENS_CARD_TYPE_OPTIONS.filter((option) => option.value !== 'insight').map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Reason for the change"
                    value={summary}
                    onChange={(event) => setSummary(event.target.value)}
                  />
                  <Button
                    variant="contained"
                    disabled={mutation.isPending || !summary.trim() || !title.trim()}
                    onClick={() =>
                      void run('proposals', {
                        baseVersion: lens.version,
                        summary,
                        definition: {
                          ...current,
                          spec: {
                            ...lens.spec,
                            cards: lens.spec.cards.map((card) =>
                              card.id === cardId ? { ...card, title, type } : card,
                            ),
                          },
                        },
                      })
                    }
                  >
                    Submit proposal
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
